<template>
  <div id="pageItemList">
    <v-layout row class="align-center layout px-4 pt-4 app--page-header">
      <div class="page-header-left">
        <h3 class="pr-3">
          <v-icon>assignment</v-icon>
          {{ title }}
        </h3>
      </div>
      <v-breadcrumbs divider="-" :items="breadcrumbs"> </v-breadcrumbs>
    </v-layout>
    <v-form ref="editCreateForm">
      <v-container grid-list-xl fluid>
        <v-layout row wrap>
          <v-flex lg12>
            <v-stepper v-model="stepper" vertical non-linear>
              <v-stepper-step
                :complete="stepper > 1"
                step="1"
                editable
                :rules="[
                  () =>
                    stepper === 1 ||
                    (!!reviewReport.vehicle &&
                      !!reviewReport.review_date &&
                      !!reviewReport.odometer &&
                      !!reviewReport.review_place),
                ]"
              >
                {{ $t("vehicle") }}
              </v-stepper-step>
              <v-stepper-content step="1">
                <v-layout wrap justify-start>
                  <v-flex xs6>
                    <v-autocomplete
                      name="vehicle"
                      :label="$t('vehicle')"
                      :loading="vehicleIsLoading"
                      :items="vehicles"
                      :search-input.sync="vehicleSearch"
                      v-model="reviewReport.vehicle"
                      v-validate="'required'"
                      cache-items
                      clearable
                      :readonly="!!reviewReport.vehicle"
                      prepend-icon="train"
                      item-text="name"
                      item-value="id"
                      :data-vv-as="$t('vehicle')"
                      :error-messages="errors.collect('vehicle')"
                      @input="fetchOdometer"
                    ></v-autocomplete>
                  </v-flex>
                  <v-flex xs2>
                    <v-menu
                      :close-on-content-click="false"
                      v-model="reviewDateMenu"
                      lazy
                      transition="scale-transition"
                      offset-y
                      full-width
                      min-width="290px"
                    >
                      <v-text-field
                        name="review_date"
                        slot="activator"
                        :value="
                          reviewReport.review_date
                            ? $moment(reviewReport.review_date).format('LL')
                            : null
                        "
                        :label="$t('reviewDate')"
                        v-validate="'required'"
                        :data-vv-as="$t('reviewDate')"
                        :error-messages="errors.collect('review_date')"
                        readonly
                      ></v-text-field>
                      <v-date-picker
                        v-model="reviewReport.review_date"
                        @input="reviewDateMenu = false"
                        :locale="LANG"
                        reactive
                        :max="$moment().format('YYYY-MM-DD')"
                      ></v-date-picker>
                    </v-menu>
                  </v-flex>
                  <v-flex xs4>
                    <v-select
                      ref="dsuSelect"
                      name="dsu"
                      v-model="reviewReport.based"
                      :items="dsuOptions"
                      :label="$t('dsu')"
                      v-validate="'required|max:255'"
                      :data-vv-as="$t('dsu')"
                      :error-messages="errors.collect('dsu')"
                    ></v-select>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      name="odometer"
                      v-model="reviewReport.odometer"
                      :label="$t('odometer')"
                      v-validate="'required|numeric'"
                      :data-vv-as="$t('odometer')"
                      :error-messages="errors.collect('odometer')"
                      type="number"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs4>
                    <v-select
                      name="review_place"
                      v-model="reviewReport.review_place"
                      :items="getAllReviewPlaces()"
                      :label="$t('reviewPlace')"
                      v-validate="'required'"
                      :data-vv-as="$t('reviewPlace')"
                      :error-messages="errors.collect('review_place')"
                    ></v-select>
                  </v-flex>
                </v-layout>
                <v-btn color="primary" @click="stepper = 2">{{
                  $t(`continue`)
                }}</v-btn>
              </v-stepper-content>

              <v-stepper-step
                :complete="stepper > 2"
                step="2"
                editable
                :rules="[() => stepper <= 2 || !!reviewReport.type]"
              >
                {{ $t("reviewType") }}
              </v-stepper-step>
              <v-stepper-content step="2">
                <v-radio-group v-model="reviewReport.type" row>
                  <v-radio
                    v-for="(item, index) in getAllReviewTypes()"
                    :key="index"
                    :label="item.text"
                    :value="item.value"
                  ></v-radio>
                </v-radio-group>
                <span v-if="reviewReport.type === 'PX'">
                  <v-text-field
                    name="subcontractor"
                    v-model="reviewReport.subcontractor"
                    :label="$t('reviewReportSubcontractor')"
                    v-validate="reviewReport.type === 'PX' ? 'required' : ''"
                    :data-vv-as="$t('reviewReportSubcontractor')"
                    :error-messages="errors.collect('subcontractor')"
                  ></v-text-field>
                  <v-text-field
                    name="subcontractor_device_name"
                    v-model="reviewReport.subcontractor_device_name"
                    :label="$t('reviewReportSubcontractorDeviceName')"
                    v-validate="reviewReport.type === 'PX' ? 'required' : ''"
                    :data-vv-as="$t('reviewReportSubcontractorDeviceName')"
                    :error-messages="
                      errors.collect('subcontractor_device_name')
                    "
                  ></v-text-field>
                </span>
                <v-checkbox
                  v-model="reviewReport.warranty_repairs"
                  :label="$t('warrantyRepairs')"
                ></v-checkbox>
                <v-btn color="primary" @click="stepper = 3">{{
                  $t(`continue`)
                }}</v-btn>
              </v-stepper-content>

              <v-stepper-step :complete="stepper > 3" step="3" editable>
                {{ $t("worklog.name") }}
              </v-stepper-step>
              <v-stepper-content step="3">
                <v-data-table
                  :headers="worklog.headers"
                  :items="reviewReportWorklogTableView"
                  class="elevation-1"
                  disable-initial-sort
                  hide-actions
                  light
                >
                  <template v-slot:items="props">
                    <td>{{ props.item.name }}</td>
                    <td>
                      <date-time-picker-dialog
                        v-model="
                          reviewReport.worklog[props.item.rowName].work_start
                        "
                      ></date-time-picker-dialog>
                    </td>
                    <td>
                      <date-time-picker-dialog
                        v-model="
                          reviewReport.worklog[props.item.rowName].work_finish
                        "
                      ></date-time-picker-dialog>
                    </td>
                    <td>
                      <date-time-picker-dialog
                        v-model="
                          reviewReport.worklog[props.item.rowName]
                            .made_review_p1
                        "
                      ></date-time-picker-dialog>
                    </td>
                    <td>
                      <date-time-picker-dialog
                        v-model="
                          reviewReport.worklog[props.item.rowName]
                            .vehicle_taken_by_occupant
                        "
                      ></date-time-picker-dialog>
                    </td>
                  </template>
                </v-data-table>
                <v-btn color="primary" @click="stepper = 4">{{
                  $t(`continue`)
                }}</v-btn>
              </v-stepper-content>

              <v-stepper-step :complete="stepper > 4" step="4" editable>
                {{ $t("consumables") }}
              </v-stepper-step>
              <v-stepper-content step="4">
                <v-textarea
                  name="used_consumables"
                  :label="$t('usedConsumables')"
                  v-model="reviewReport.used_consumables"
                  v-validate="'required'"
                  :data-vv-as="$t('usedConsumables')"
                  :error-messages="errors.collect('used_consumables')"
                  auto-grow
                  rows="2"
                ></v-textarea>

                <v-btn color="primary" @click.native="saveAction">{{
                  isEditView()
                    ? $t("editReviewReport")
                    : $t("createReviewReport")
                }}</v-btn>
              </v-stepper-content>
            </v-stepper>
          </v-flex>
        </v-layout>
      </v-container>
    </v-form>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import store from "@/store";
import {
  FETCH_FLEETS,
  CREATE_REVIEW_REPORT,
  FETCH_REVIEW_REPORT,
  FETCH_VEHICLES,
  RESET_REVIEW_REPORT,
  SHOW_SNACKBAR,
  UPDATE_REVIEW_REPORT,
} from "../../store/actions.type";
import validationErrorsMixin from "@/mixins/validationErrors.mixin";
import _ from "lodash";
import ReviewPlaces from "@/common/enums/reviewPlace.service";
import ReviewReportTypes from "@/common/enums/reviewReportType.service";
import DateTimePickerDialog from "@/components/dialog/DateTimePickerDialog.vue";
import { PARKING_STATION } from "../../common/enums/reviewPlace";

export default {
  data() {
    return {
      reviewDateMenu: false,
      stepper: 1,
      LANG: process.env.VUE_APP_LANG,
      DSU: process.env.VUE_APP_DSU,
      vehicleSearch: "",
      formDisabled: false,
      breadcrumbs: [
        {
          text: this.$t("reviewReports"),
          to: { name: "ReviewReports" },
          exact: true,
        },
      ],
      worklog: {
        headers: [
          { text: "", sortable: false },
          { text: this.$t("reviewReportWorklog.workStart"), sortable: false },
          { text: this.$t("reviewReportWorklog.workFinish"), sortable: false },
          {
            text: this.$t("reviewReportWorklog.madeReviewP1"),
            sortable: false,
          },
          {
            text: this.$t("reviewReportWorklog.vehicleTakenByOccupant"),
            sortable: false,
          },
        ],
        value: null,
        date: null,
        time: null,
      },
    };
  },
  computed: {
    ...mapGetters([
      "reviewReport",
      "reviewReportWorklogTableView",
      "vehicles",
      "vehicle",
      "vehiclesCount",
      "vehicleIsLoading",
      "fleets",
      "fleetsCount",
      "fleet",
      "fleetIsLoading",
    ]),

    selectedDsu: {
      get() {
        // Pobiera wartość floty na podstawie reviewReport.based
        const selectedFleet = this.fleets.find(
          (f) => f.dsu === this.reviewReport.based
        );
        return selectedFleet ? selectedFleet.dsu : null;
      },
      set(value) {
        // Ustawia wartość reviewReport.based na podstawie wyboru użytkownika
        this.reviewReport.based = value;
      },
    },

    dsuOptions() {
      return this.fleets.map((fleet) => ({
        text: `${fleet.name} (${fleet.dsu})`,
        value: fleet.dsu,
      }));
    },
    title() {
      return !this.isEditView()
        ? this.$t("createReviewReport")
        : this.$t("editReviewReport") + " #" + this.reviewReport.rma;
    },
  },
  mixins: [validationErrorsMixin],
  watch: {
    vehicleSearch: _.debounce(function (val) {
      this.$store.dispatch(FETCH_VEHICLES, [{ rowsPerPage: -1 }, val]);
    }, 300),
    "reviewReport.vehicle": function (newVal) {
      console.log("Watcher reviewReport.vehicle wywołany z wartością:", newVal);
      if (newVal) {
        const selectedVehicle = this.vehicles.find((v) => v.id === newVal);
        console.log("Znaleziony pojazd:", selectedVehicle);
        if (selectedVehicle) {
          const matchingFleet = this.fleets.find(
            (f) => f.id === selectedVehicle.fleet
          );
          console.log("Znaleziono flotę:", matchingFleet);
          if (matchingFleet) {
            this.reviewReport.based = matchingFleet.dsu;
            console.log("DSU ustawione na:", matchingFleet.dsu);
          } else {
            this.reviewReport.based = "";
            console.log("Brak pasującej floty, DSU wyczyszczone");
          }
        }
      } else {
        this.reviewReport.based = null;
        console.log("Pojazd wyczyszczony, DSU wyczyszczone");
      }
      this.$nextTick(() => {
        if (this.$refs.dsuSelect) {
          this.$refs.dsuSelect.$forceUpdate();
          console.log("Wymuszone odświeżenie DSU");
        }
      });
    },
    immediate: true,
  },

  components: {
    "date-time-picker-dialog": DateTimePickerDialog,
  },
  created: function () {
    if (this.isCreateView()) {
      this.reviewReport.review_place = PARKING_STATION;
    }
  },
  methods: {
    getAllReviewPlaces() {
      return ReviewPlaces.getAll();
    },
    getAllReviewTypes() {
      return ReviewReportTypes.getAll();
    },
    isEditView() {
      return !!this.$route.params.id;
    },
    saveAction() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.formDisabled = true;

          if (_.isObject(this.reviewReport.vehicle)) {
            this.reviewReport.vehicle = this.reviewReport.vehicle.id;
          }

          this.$store
            .dispatch(
              this.isEditView() ? UPDATE_REVIEW_REPORT : CREATE_REVIEW_REPORT,
              this.reviewReport
            )
            .then(() => {
              this.$store.dispatch(SHOW_SNACKBAR, {
                text: this.$t("savedSuccessfully", [this.$t("reviewReport")]),
                color: "success",
              });

              this.$store.dispatch(RESET_REVIEW_REPORT);
              this.$refs.editCreateForm.reset();
              this.$validator.reset();
              this.$router.push({ name: "ReviewReports" });
            })
            .catch(() => {
              this.formDisabled = false;
            });
        }
      });
    },
    fetchOdometer(vehicleId) {
      if (vehicleId) {
        this.reviewReport.odometer = this.vehicles.find(
          (vehicle) => vehicle.id === vehicleId
        ).odometer;
      } else {
        this.reviewReport.odometer = null;
        this.reviewReport.based = null;
      }
    },
    isCreateView() {
      return !this.$route.params.id;
    },
  },
  beforeRouteEnter(to, from, next) {
    Promise.all([
      store.dispatch(RESET_REVIEW_REPORT),
      store.dispatch(FETCH_FLEETS, [{ rowsPerPage: -1 }]),
    ]).then(() => {
      if (to.params.id) {
        store.dispatch(FETCH_REVIEW_REPORT, to.params.id).then(() => {
          next();
        });
      } else {
        next();
      }
    });
  },
};
</script>

<style scoped></style>
